import * as THREE from 'three';

export interface BlockType {
  id: number;
  name: string;
  color: number;
  topColor?: number;
  bottomColor?: number;
  transparent: boolean;
  solid: boolean;
  isItem?: boolean; // true = item only, cannot be placed as block
  dropsItem?: number; // item id dropped when block is broken (defaults to self)
  interactable?: boolean; // true = right-click triggers interaction instead of placing
  lightLevel?: number; // 0-15, light emitted by this block (for lightmap)
}

export const BLOCKS: Record<number, BlockType> = {
  0: { id: 0, name: 'Lucht', color: 0x000000, transparent: true, solid: false },
  1: { id: 1, name: 'Gras', color: 0x5b8731, topColor: 0x5b8731, bottomColor: 0x8b6914, transparent: false, solid: true },
  2: { id: 2, name: 'Aarde', color: 0x8b6914, transparent: false, solid: true },
  3: { id: 3, name: 'Steen', color: 0x888888, transparent: false, solid: true, dropsItem: 8 },
  4: { id: 4, name: 'Zand', color: 0xdbd3a0, transparent: false, solid: true },
  5: { id: 5, name: 'Hout', color: 0x9c7a4a, transparent: false, solid: true },
  6: { id: 6, name: 'Bladeren', color: 0x3a7d22, transparent: false, solid: true },
  7: { id: 7, name: 'Water', color: 0x3366cc, transparent: true, solid: false },
  8: { id: 8, name: 'Kasseien', color: 0x777777, transparent: false, solid: true },
  9: { id: 9, name: 'Planken', color: 0xbc9458, transparent: false, solid: true },
  10: { id: 10, name: 'Glas', color: 0xccddee, transparent: true, solid: true },
  11: { id: 11, name: 'Baksteen', color: 0xb04030, transparent: false, solid: true },
  12: { id: 12, name: 'Sneeuw', color: 0xeeeeff, transparent: false, solid: true },
  13: { id: 13, name: 'Bloem', color: 0xff4466, transparent: true, solid: false },
  14: { id: 14, name: 'Steenkoolerts', color: 0x555555, transparent: false, solid: true, dropsItem: 28 },
  15: { id: 15, name: 'IJzererts', color: 0xccaa88, transparent: false, solid: true, dropsItem: 21 },
  16: { id: 16, name: 'Gouderts', color: 0xffdd44, transparent: false, solid: true, dropsItem: 22 },
  17: { id: 17, name: 'Diamanterts', color: 0x44ffee, transparent: false, solid: true, dropsItem: 23 },
  18: { id: 18, name: 'Bodemsteen', color: 0x333333, transparent: false, solid: true },
  19: { id: 19, name: 'Stok', color: 0x8b6c3a, transparent: true, solid: false, isItem: true },
  20: { id: 20, name: 'Fakkel', color: 0xffcc00, transparent: true, solid: false, lightLevel: 15 },
  21: { id: 21, name: 'IJzerstaaf', color: 0xddccbb, transparent: true, solid: false, isItem: true },
  22: { id: 22, name: 'Goudstaaf', color: 0xffdd44, transparent: true, solid: false, isItem: true },
  23: { id: 23, name: 'Diamant', color: 0x44ffee, transparent: true, solid: false, isItem: true },
  24: { id: 24, name: 'Werktafel', color: 0xbc8458, topColor: 0xaa7744, bottomColor: 0x9c7a4a, transparent: false, solid: true },
  25: { id: 25, name: 'Oven', color: 0x888888, topColor: 0x666666, bottomColor: 0x777777, transparent: false, solid: true },
  26: { id: 26, name: 'Houten Houweel', color: 0xbc9458, transparent: true, solid: false, isItem: true },
  27: { id: 27, name: 'IJzeren Houweel', color: 0xddccbb, transparent: true, solid: false, isItem: true },
  28: { id: 28, name: 'Steenkool', color: 0x333333, transparent: true, solid: false, isItem: true },
  29: { id: 29, name: 'Zandsteen', color: 0xd4c494, transparent: false, solid: true },
  30: { id: 30, name: 'IJs', color: 0xcceeff, transparent: true, solid: true },
  31: { id: 31, name: 'Cactus', color: 0x2d8a4e, transparent: false, solid: true },
  32: { id: 32, name: 'Paddenstoel', color: 0xcc8844, transparent: true, solid: false },
  33: { id: 33, name: 'Lang Gras', color: 0x4a7a2a, transparent: true, solid: false },
  34: { id: 34, name: 'Sparrenhout', color: 0x6b4f32, transparent: false, solid: true },
  35: { id: 35, name: 'Sparrenbladeren', color: 0x2a5a1a, transparent: false, solid: true },
  36: { id: 36, name: 'Berkenhout', color: 0xe8dcc8, transparent: false, solid: true },
  37: { id: 37, name: 'Berkenbladeren', color: 0x5ca03a, transparent: false, solid: true },
  38: { id: 38, name: 'Donker Gras', color: 0x3d6b24, topColor: 0x3d6b24, bottomColor: 0x8b6914, transparent: false, solid: true },
  39: { id: 39, name: 'Liaan', color: 0x2d6b1a, transparent: true, solid: false },
  40: { id: 40, name: 'Lava', color: 0xff4400, transparent: true, solid: false, lightLevel: 15 },
  41: { id: 41, name: 'Grind', color: 0x9a9a9a, transparent: false, solid: true },
  42: { id: 42, name: 'Obsidiaan', color: 0x1a0a2e, transparent: false, solid: true },
  43: { id: 43, name: 'Klei', color: 0xb0a090, transparent: false, solid: true },
  44: { id: 44, name: 'Vliegblok', color: 0x44ffcc, topColor: 0x66ffee, bottomColor: 0x228866, transparent: false, solid: true },
  45: { id: 45, name: 'Supervliegblok', color: 0xff44cc, topColor: 0xff66ee, bottomColor: 0x882266, transparent: false, solid: true },
  46: { id: 46, name: 'Portaalsteen', color: 0x2a0845, transparent: false, solid: true },
  47: { id: 47, name: 'Portaalvlak', color: 0x9b59b6, transparent: true, solid: false, lightLevel: 11 },
  48: { id: 48, name: 'Bed', color: 0xcc3333, topColor: 0xcc3333, bottomColor: 0x8b6914, transparent: true, solid: true, interactable: true },
  49: { id: 49, name: 'BedHoofd', color: 0xaa2222, topColor: 0xaa2222, bottomColor: 0x8b6914, transparent: true, solid: true, dropsItem: 48, interactable: true },
  50: { id: 50, name: 'Springplank', color: 0xff4444, topColor: 0xff6666, bottomColor: 0xaa2222, transparent: false, solid: true },
  51: { id: 51, name: 'Verfblok Rood', color: 0xff3333, transparent: true, solid: false },
  52: { id: 52, name: 'Verfblok Blauw', color: 0x3333ff, transparent: true, solid: false },
  53: { id: 53, name: 'Verfblok Groen', color: 0x33ff33, transparent: true, solid: false },
  54: { id: 54, name: 'Verfblok Geel', color: 0xffff33, transparent: true, solid: false },
  55: { id: 55, name: 'Pijlblok', color: 0xff8800, topColor: 0xffaa44, bottomColor: 0xcc6600, transparent: false, solid: true },
  56: { id: 56, name: 'Pijlblok Oost', color: 0xff8800, topColor: 0xffaa44, bottomColor: 0xcc6600, transparent: false, solid: true, dropsItem: 55 },
  57: { id: 57, name: 'Pijlblok Zuid', color: 0xff8800, topColor: 0xffaa44, bottomColor: 0xcc6600, transparent: false, solid: true, dropsItem: 55 },
  58: { id: 58, name: 'Pijlblok West', color: 0xff8800, topColor: 0xffaa44, bottomColor: 0xcc6600, transparent: false, solid: true, dropsItem: 55 },
  59: { id: 59, name: 'Muziekblok', color: 0xcc66ff, topColor: 0xdd88ff, bottomColor: 0x9944cc, transparent: false, solid: true, interactable: true },
  60: { id: 60, name: 'Teleporteerblok', color: 0x00ccff, topColor: 0x44ddff, bottomColor: 0x0088aa, transparent: false, solid: true, lightLevel: 8 },
  61: { id: 61, name: 'Explosiefblok', color: 0xff0000, topColor: 0xff4444, bottomColor: 0xcc0000, transparent: false, solid: true, interactable: true },
  62: { id: 62, name: 'Vuurwerk', color: 0xff6600, topColor: 0xff8844, bottomColor: 0xcc4400, transparent: false, solid: true, interactable: true },
  63: { id: 63, name: 'Snelheidsblok', color: 0x00ff88, topColor: 0x44ffaa, bottomColor: 0x00aa55, transparent: false, solid: true },
};

export const HOTBAR_BLOCKS = [1, 2, 3, 4, 5, 9, 8, 11, 10];
export const ALL_PLACEABLE = [1, 2, 3, 4, 5, 6, 8, 9, 10, 11, 12, 20, 24, 25, 29, 30, 31, 34, 35, 36, 37, 38, 41, 42, 43, 44, 45, 46, 48, 50, 51, 52, 53, 54, 55, 59, 60, 61, 62, 63];
export const ALL_ITEMS: number[] = Object.values(BLOCKS)
  .filter(b => b.id !== 0)
  .map(b => b.id);

const materialCache = new Map<string, THREE.MeshLambertMaterial>();

export function getBlockMaterial(blockId: number, face: 'top' | 'bottom' | 'side'): THREE.MeshLambertMaterial {
  const block = BLOCKS[blockId];
  if (!block) return new THREE.MeshLambertMaterial({ color: 0xff00ff });

  let color = block.color;
  if (face === 'top' && block.topColor) color = block.topColor;
  if (face === 'bottom' && block.bottomColor) color = block.bottomColor;

  const key = `${blockId}-${face}`;
  if (materialCache.has(key)) return materialCache.get(key)!;

  const mat = new THREE.MeshLambertMaterial({
    color,
    transparent: block.transparent,
    opacity: block.transparent ? (blockId === 7 ? 0.6 : blockId === 40 ? 0.9 : blockId === 30 ? 0.7 : 0.8) : 1.0,
    side: THREE.FrontSide,
  });
  materialCache.set(key, mat);
  return mat;
}
